
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Domain } from "@/types/domain";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { FileText, ExternalLink, ChevronDown, ChevronUp, FolderClosed } from "lucide-react";

interface KnowledgeBase {
  title: string;
  url: string;
  description?: string;
  isAccessible?: boolean;
}

interface DomainBoxProps {
  domain: Domain;
  backgroundColor?: string;
  getKnowledgeBases?: (symbol: string) => KnowledgeBase[];
  onDomainClick?: (domain: Domain) => void;
  kbCount?: number;
}

// Sample knowledge bases for the Drilling domain
const getDrillingKnowledgeBases = (symbol: string): KnowledgeBase[] => {
  if (symbol === "Drilling") {
    return [
      // Accessible KBs (Chevron)
      { 
        title: "Chevron Drilling Standards", 
        url: `/chevron-solutions?q=${encodeURIComponent("Chevron Drilling Standards")}`,
        isAccessible: true 
      },
      { 
        title: "Well Design Best Practices", 
        url: `/chevron-solutions?q=${encodeURIComponent("Well Design Best Practices")}`,
        isAccessible: true 
      },
      { 
        title: "Blowout Prevention Protocols", 
        url: `/chevron-solutions?q=${encodeURIComponent("Blowout Prevention Protocols")}`,
        isAccessible: true 
      },
      // Non-accessible KBs (Other companies)
      { 
        title: "ExxonMobil Drilling Guidelines", 
        url: "#",
        isAccessible: false 
      },
      { 
        title: "Shell Offshore Drilling Manual", 
        url: "#",
        isAccessible: false 
      },
      { 
        title: "BP Deep Water Operations", 
        url: "#",
        isAccessible: false 
      }
    ];
  }
  return defaultKnowledgeBases(symbol);
};

// Default knowledge bases if none provided
const defaultKnowledgeBases = (symbol: string): KnowledgeBase[] => {
  return [
    { title: "Sample Knowledge Base 1", url: `/curt-solutions?q=${encodeURIComponent("Sample Knowledge Base 1")}` },
    { title: "Sample Knowledge Base 2", url: `/curt-solutions?q=${encodeURIComponent("Sample Knowledge Base 2")}` },
    { title: "Sample Knowledge Base 3", url: `/curt-solutions?q=${encodeURIComponent("Sample Knowledge Base 3")}` }
  ];
};

export const DomainBox = ({ 
  domain, 
  backgroundColor = "bg-primary", 
  getKnowledgeBases = domain.symbol === "Drilling" ? getDrillingKnowledgeBases : defaultKnowledgeBases,
  onDomainClick
}: DomainBoxProps) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const handleDomainClick = () => {
    setExpanded(!expanded);
    if (onDomainClick) {
      onDomainClick(domain);
    }
  };

  const handleKnowledgeBaseClick = (url: string, isAccessible: boolean | undefined, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isAccessible) {
      // Open internal marketplace links with React Router; external in new tab
      if (url.startsWith("/")) {
        navigate(url);
      } else {
        window.open(url, "_blank");
      }
    }
  };

  const knowledgeBases = getKnowledgeBases(domain.symbol);
  const accessibleKBs = knowledgeBases.filter(kb => kb.isAccessible !== false);
  const nonAccessibleKBs = knowledgeBases.filter(kb => kb.isAccessible === false);

  return (
    <div className="flex flex-col">
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div 
              className={`${backgroundColor} text-primary-foreground p-4 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:bg-black hover:shadow-lg flex flex-col items-center relative`}
              onClick={handleDomainClick}
            >
              <div className="text-sm font-medium text-center">{domain.name}</div>
              <div className="absolute right-2 top-2">
                {expanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent 
            side="right"
            className="bg-white text-black border border-gray-200 max-w-[400px] whitespace-pre-line"
          >
            <p className="font-semibold mb-2">{domain.name}</p>
            <p className="text-sm">Representative Knowledge Bases:</p>
            <p className="text-xs mt-1">{domain.categories}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {expanded && (
        <div className="mt-2 bg-white rounded-lg shadow-md p-3 border border-gray-200 z-10">
          {accessibleKBs.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                <FolderClosed className="h-4 w-4" />
                <span>Accessible Knowledge Bases</span>
              </div>
              <div className="space-y-2">
                {accessibleKBs.map((kb, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer text-xs"
                    onClick={(e) => handleKnowledgeBaseClick(kb.url, kb.isAccessible, e)}
                  >
                    <FileText className="h-3 w-3 text-black flex-shrink-0" />
                    <span className="flex-1 break-words leading-tight">{kb.title}</span>
                    <ExternalLink className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {nonAccessibleKBs.length > 0 && (
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                <FolderClosed className="h-4 w-4 text-gray-400" />
                <span className="text-gray-500">Other Industry Knowledge Bases</span>
              </div>
              <div className="space-y-2">
                {nonAccessibleKBs.map((kb, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-2 p-2 bg-gray-100 rounded text-xs text-gray-500 cursor-not-allowed"
                  >
                    <FileText className="h-3 w-3 text-gray-400 flex-shrink-0" />
                    <span className="flex-1 break-words leading-tight">{kb.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
